import { Customer, CustomerAdd } from "@/types/sales";
import { App, Button, Flex, Input, Tooltip, Modal, Spin, Progress } from "antd";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useDataList from "@/hooks/useDataList";
import customerApi from "@/apis/customer";
import { CommonListParams } from "@/types/common";
import useDataUpdate from "@/hooks/useDataUpdate";
import useDataAdd from "@/hooks/useDataAdd";
import useDataDel from "@/hooks/useDataDel";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import useCustomerColumn from "../hooks/useCustomerColumn";
import CustomerForm from "./SCustomerForm";
import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef
} from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { CustomTabPaneContext } from "@/components/TabPane";
import { getStorage } from "@/utils/storage";
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";
import { countries } from "@/utils/country";
import { DictContext } from "@/layout/context/DictContext";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

interface CustomerFilters {
  keyword: string;
}

const INITIAL_CUSTOMER_FILTERS: CustomerFilters = {
  keyword: "",
};

const CustomerTable: FC = () => {
  const { t } = useTranslation();

  const { onOpen } = useContext(CustomTabPaneContext);
  const {
    currencys,
    saleTradeTerm,
    salePayMethod,
    saleTypes,
    creditLevels,
    orgs,
    saleCustomerType
  } = useContext(DictContext);

  // 导出列配置
  const customerColumns = [
    { key: 'sale.code', label: t('sale.code') },
    { key: 'sale.principal', label: t('sale.principal') },
    { key: 'sale.name1', label: t('sale.name1') },
    { key: 'sale.name2', label: t('sale.name2') },
    { key: 'sale.shortName', label: t('sale.shortName') },
    { key: 'sale.customerType', label: t('sale.customerType') },
    { key: 'sale.country', label: t('sale.country') },
    { key: 'sale.address', label: t('sale.address') },
    { key: 'sale.zipCode', label: t('sale.zipCode') },
    { key: 'sale.email', label: t('sale.email') },
    { key: 'sale.contact', label: t('sale.contact') },
    { key: 'sale.phone', label: t('sale.phone') },
    { key: 'sale.fax', label: t('sale.fax') },
    { key: 'sale.mobile', label: t('sale.mobile') },
    { key: 'sale.account1', label: t('sale.account1') },
    { key: 'sale.bank1', label: t('sale.bank1') },
    { key: 'sale.swiftCode1', label: t('sale.swiftCode1') },
    { key: 'sale.account2', label: t('sale.account2') },
    { key: 'sale.bank2', label: t('sale.bank2') },
    { key: 'sale.swiftCode2', label: t('sale.swiftCode2') },
    { key: 'sale.currency', label: t('sale.currency') },
    { key: 'sale.saleType', label: t('sale.saleType') },
    { key: 'sale.creditLevel', label: t('sale.creditLevel') },
    { key: 'sale.creditLine', label: t('sale.creditLine') },
    { key: 'sale.depositRate', label: t('sale.depositRate') },
    { key: 'sale.tradeTerm', label: t('sale.tradeTerm') },
    { key: 'sale.payMethod', label: t('sale.payMethod') },
    { key: 'sale.commissionRate', label: t('sale.commissionRate') },
    { key: 'sale.affiliates', label: t('sale.affiliates') },
    { key: 'sale.org', label: t('sale.org') },
    { key: 'sale.billingDays', label: t('sale.billingDays') },
    { key: 'sale.taxRate', label: t('sale.taxRate') },
  ];

  // 导出数据映射函数
  const customerDataMapper = (item: Customer, t: any, context: any) => ({
    [t('sale.code')]: item?.code,
    [t('sale.principal')]: item?.principal,
    [t('sale.name1')]: item?.name1,
    [t('sale.name2')]: item?.name2,
    [t('sale.shortName')]: item?.shortName,
    [t('sale.customerType')]: context.saleCustomerType.find((ct: any) => ct.id === item?.type)?.key,
    [t('sale.country')]: countries.find(country => country.code === item?.country)?.name,
    [t('sale.address')]: item?.address,
    [t('sale.zipCode')]: item?.zipCode,
    [t('sale.email')]: item?.email,
    [t('sale.contact')]: item?.contact,
    [t('sale.phone')]: item?.phone,
    [t('sale.fax')]: item?.fax,
    [t('sale.mobile')]: item?.mobile,
    [t('sale.account1')]: item?.account1,
    [t('sale.bank1')]: item?.bank1,
    [t('sale.swiftCode1')]: item?.swiftCode1,
    [t('sale.account2')]: item?.account2,
    [t('sale.bank2')]: item?.bank2,
    [t('sale.swiftCode2')]: item?.swiftCode2,
    [t('sale.currency')]: context.currencys.find((currency: any) => currency.key === item?.currency)?.desc,
    [t('sale.saleType')]: context.saleTypes.find((st: any) => st.id === item?.saleType)?.key,
    [t('sale.creditLevel')]: context.creditLevels.find((cl: any) => cl.key === item?.creditLevel)?.desc,
    [t('sale.creditLine')]: item?.creditLine,
    [t('sale.depositRate')]: item?.depositRate ? item?.depositRate + '%' : '',
    [t('sale.tradeTerm')]: context.saleTradeTerm.find((tt: any) => tt.id === item?.tradeTerm)?.key,
    [t('sale.payMethod')]: context.salePayMethod.find((pm: any) => pm.id === item?.payMethod)?.key,
    [t('sale.commissionRate')]: item?.commissionRate ? item?.commissionRate + '%' : '',
    [t('sale.affiliates')]: item?.affiliates,
    [t('sale.org')]: context.orgs.find((org: any) => org.id === item?.orgId)?.name,
    [t('sale.billingDays')]: item?.billingDays,
    [t('sale.taxRate')]: item?.taxRate ? item?.taxRate + '%' : '',
  });
  const { notification } = App.useApp();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const [list, total, listLoading, { fetchList, addItem, rmItem, updateItem }] =
    useDataList<Customer, [string, CommonListParams]>({
      fetchApi: customerApi.list
    });
  const { update: updatePrice, loading: updateLoading } = useDataUpdate<
    [CustomerAdd, string]
  >({ api: customerApi.update });
  const { add, loading: addLoading } = useDataAdd<[CustomerAdd], Customer>({
    api: customerApi.add
  });
  const { del } = useDataDel({ api: customerApi.del });

  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<CustomerFilters>(INITIAL_CUSTOMER_FILTERS);
  const { keyword } = draftFilters;
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [detail, setDetail] = useState<Customer | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const { onConfirm } = useFrontDelTip({ cb: (id: string) => onDel(id) });
  const columns = useCustomerColumn({
    onDel: onConfirm,
    onDetail: setDetail,
    onOpenTab: onOpen
  });

  useEffect(() => {
    fetch(0, INITIAL_CUSTOMER_FILTERS);
  }, []);

  const onDel = (id: string) => {
    return del(id)
      .then(() => {
        rmItem(id);
        notification.success({ placement: 'top', message: t("common.rmSuccess") });
      })
      .catch(() => {
        notification.error({ placement: 'top', message: t("common.rmError") });
      });
  };

  const fetch = (
    offset: number,
    filters: CustomerFilters,
  ) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    fetchList(getStorage("curOrgId"), {
      body: filters.keyword,
      pagination: { offset, take }
    }).catch(err => {
      switch (err) {
        case 10010:
          break;
        default:
          break;
      }
    });
  };

  const handleExportAll = async (): Promise<Customer[]> => {
    const batchSize = 100;
    const allDataList: Customer[] = [];

    setExportLoading(true);
    setExportProgress(0);

    try {
      // 先发起第一次请求获取总条数
      const firstRes = await customerApi.list(getStorage("curOrgId"), {
        body: appliedFilters.keyword,
        pagination: { offset: 0, take: batchSize }
      });

      if (firstRes?.body) {
        allDataList.push(...firstRes.body);
      }

      // 根据返回的 pagination.size 计算总批次数
      const totalSize = firstRes?.pagination?.size ?? 0;
      const batches = Math.ceil(totalSize / batchSize);

      // 继续请求剩余数据
      for (let i = 1; i < batches; i++) {
        const offset = i * batchSize;
        const res = await customerApi.list(getStorage("curOrgId"), {
          body: appliedFilters.keyword,
          pagination: { offset, take: batchSize }
        });
        if (res?.body) {
          allDataList.push(...res.body);
        }
        // 更新进度
        setExportProgress(Math.round(((i + 1) / batches) * 100));
      }
      return allDataList;
    } catch (error) {
      notification.error({ placement: 'top',
        message: t('common.exportErr'),
        description: `${t('requirement.total')}: ${allDataList.length}`
      });
      throw error;
    } finally {
      setExportLoading(false);
      setExportProgress(0);
    }
  };

  const handleSearch = () => {
    const nextFilters = commitFilters();
    fetch(0, nextFilters);
    tableRef.current?.resetTablePage();
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_CUSTOMER_FILTERS);
    fetch(0, nextFilters);
    tableRef.current?.resetTablePage();
  };

  const headerChildren = (
    <Flex style={{ width: "100%" }} justify="space-between">
      <Flex gap={10}>
        <Button
          onClick={() => setAddOpen(true)}
          type="primary"
          style={{ marginBottom: 16 }}
        >
          {t("sale.addCustomer")}
        </Button>
        <ExportExcelButton<Customer>
          list={list}
          columns={customerColumns}
          defaultSelectedColumns={['sale.code', 'sale.shortName', 'sale.name1']}
          dataMapper={customerDataMapper}
          fileName="customers.xlsx"
          sheetName="Sheet1"
          context={{
            currencys,
            saleTradeTerm,
            salePayMethod,
            saleTypes,
            creditLevels,
            orgs,
            saleCustomerType
          }}
        />
        <ExportExcelButton<Customer>
          list={list}
          isAll
          loadData={handleExportAll}
          columns={customerColumns}
          defaultSelectedColumns={['sale.code', 'sale.shortName', 'sale.name1']}
          dataMapper={customerDataMapper}
          fileName="customers.xlsx"
          sheetName="Sheet1"
          context={{
            currencys,
            saleTradeTerm,
            salePayMethod,
            saleTypes,
            creditLevels,
            orgs,
            saleCustomerType
          }}
        />
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={listLoading}
        >
          {({ submit }) => (
            <Tooltip title={t("sale.bySearchCustomer")}>
              <Input
                placeholder={t("sale.bySearchCustomer")}
                style={{ width: 200 }}
                value={keyword}
                onChange={event =>
                  updateDraftFilters({ keyword: event.target.value.trim() })
                }
                onPressEnter={submit}
              />
            </Tooltip>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        loading={listLoading}
        dataSource={list}
        columns={columns as ColumnsType<AnyObject>}
        scrollX={4200}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={offset => fetch(offset, appliedFilters)}
        extHeight={50}
      />
      <CustomerForm
        key={"addCustom"}
        open={addOpen}
        confirmLoading={addLoading}
        handleCancel={() => setAddOpen(false)}
        handleOk={data => {
          return new Promise((resolve, reject) => {
            add({ ...data, code: data.code.trim() })
              .then(res => {
                addItem(res);
                setAddOpen(false);
                notification.success({ placement: 'top', message: t("common.addSuccess") });
                resolve();
              })
              .catch(err => {
                const map: {
                  [key in number]: string;
                } = {
                  10010: t("common.networkError"),
                  71001: t("sale.codeExisted")
                };
                notification.error({ placement: 'top',
                  message: t("common.addError"),
                  description: map[err] ?? t("common.serverError")
                });
              });
          });
        }}
        leftTitle={t("common.add")}
        data={null}
      />
      <CustomerForm
        key={detail?.id}
        open={!!detail}
        confirmLoading={updateLoading}
        handleCancel={() => setDetail(null)}
        handleOk={data => {
          return new Promise((resolve, reject) => {
            if (detail) {
              updatePrice(data, detail.id)
                .then(() => {
                  updateItem({ ...detail, ...data });
                  setDetail(null);
                  notification.success({ placement: 'top', message: t("sale.updateSuccess") });
                  resolve();
                })
                .catch(err => {
                  const map: {
                    [key in number]: string;
                  } = {
                    10010: t("common.networkError")
                  };
                  notification.error({ placement: 'top',
                    message: t("common.addError"),
                    description: map[err.code] ?? t("common.serverError")
                  });
                  reject();
                });
            }
          });
        }}
        leftTitle={t("common.edit")}
        data={detail}
      />
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
    </Flex>
  );
};

export default CustomerTable;
